import React, { ErrorInfo } from 'react'
import { createRoot } from 'react-dom/client'
import App from 'App'
import './index.css'
import * as serviceWorker from './serviceWorker'
import { initializeIcons } from '@fluentui/react/lib/Icons'
import { initializeFileTypeIcons } from '@fluentui/react-file-type-icons'
import { useStore } from './store/app'
import { IConfigurationState } from "./interfaces/configuration";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import { MsalContextProvider } from "./store/MsalContextProvider";


interface IErrorBoundaryProps {
  children?: React.ReactNode;
}

class ErrorBoundary extends React.Component<IErrorBoundaryProps> {
  state = { error: null as Error | null, info: null as ErrorInfo | null }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ error, info })
  }

  render() {
    if (this.state.error) {
      // Render fallback UI
      return (
          <div>
            <h1>Er is een onverwachte fout opgetreden</h1>
            <p>{JSON.stringify(this.state.error)}</p>
            <p>{JSON.stringify(this.state.info)}</p>
          </div>
      )
    }
    return this.props.children
  }
}

const documentReady = (): Promise<void> => {
  return new Promise<void>((resolve) => {
    if (/complete|interactive/.test(document.readyState)) {
      resolve();
    } else {
      window.addEventListener(`DOMContentLoaded`, () => resolve());
    }
  });
}

const startup = async () => {
  initializeIcons()
  initializeFileTypeIcons()

  let fetchStaticSettings: Promise<Response>
  if (process.env.NODE_ENV === 'development') {
    fetchStaticSettings = fetch(process.env.PUBLIC_URL + '/settings.dev.json')
  } else {
    fetchStaticSettings = fetch(process.env.PUBLIC_URL + '/settings.json')
  }

  await documentReady()
  const fetchStaticSettingsResp = await fetchStaticSettings
  const staticSettings: IConfigurationState = await fetchStaticSettingsResp.json()
  const fetchDynamicSettingsResp = await fetch(`${staticSettings.apiEndpoint}/api/abp/frontendconfig/portal`)
  const configData = await fetchDynamicSettingsResp.json();
  const settings: IConfigurationState = {
    ...configData,
    apiEndpoint: staticSettings.apiEndpoint,
  }
  
  useStore.setState({ configuration: { ...settings, initialized: true } });

  const queryClient = new QueryClient();
  const container = document.getElementById("root");
  const root = createRoot(container!)
  root.render(
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <MsalContextProvider>
          <App />
        </MsalContextProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )

  if (process.env.NODE_ENV === 'development' && (module as any).hot) {
    (module as any).hot.accept('./App', () => {
      const nextApp = require('./App').default
      root.render(nextApp)
    })
  }
}

startup().catch(error => console.log('Error during startup: ', error))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();